import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from 'utils/cn';

const Popover = PopoverPrimitive.Root;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverContent = React.forwardRef<
    React.ElementRef<typeof PopoverPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => {
    const localRef = React.useRef<HTMLDivElement>(null);
    // Allow both forwarded and local refs, always return HTMLDivElement (never undefined)
    React.useImperativeHandle(ref, () => localRef.current!);

    // Helper to find the first scrollable child
    const getScrollable = (node: HTMLElement | null) => {
        if (!node) return null;
        // Prefer .overflow-y-auto or .scrollable
        const query = node.querySelector('.overflow-y-auto, .scrollable');
        if (query) return query;
        return (
            Array.from(node.children).find(
                (el): el is HTMLElement =>
                    el.scrollHeight > el.clientHeight &&
                    (getComputedStyle(el).overflowY === 'auto' || getComputedStyle(el).overflowY === 'scroll'),
            ) || null
        );
    };

    const handleWheel = (e: React.WheelEvent) => {
        const scrollable = getScrollable(localRef.current);
        if (scrollable) {
            scrollable.scrollTop += e.deltaY;
            if (
                (e.deltaY < 0 && scrollable.scrollTop > 0) ||
                (e.deltaY > 0 && scrollable.scrollTop + scrollable.clientHeight < scrollable.scrollHeight)
            ) {
                // Only prevent default if the event is cancelable
                if (e.cancelable) {
                    e.preventDefault();
                }
                e.stopPropagation();
            }
        }
    };
    const handleTouchMove = () => {
        // Let browser handle, just don't block
    };

    return (
        <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
                ref={localRef}
                align={align}
                sideOffset={sideOffset}
                className={cn(
                    'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-70 w-72 rounded-md border p-4 shadow-md outline-none',
                    className,
                )}
                onWheel={handleWheel}
                onTouchMove={handleTouchMove}
                {...props}
            />
        </PopoverPrimitive.Portal>
    );
});
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverAnchor, PopoverTrigger, PopoverContent };
